import {differenceInSeconds, parse} from "date-fns";
import {getAssetPath} from "../../../../utils/get-asset-path.js";

export const initialOptions = {
    groupByPlate: false,
    showTimeBetweenPassage: false,
    groupByMarker: false,
    orderAlphabetically: false,
}

export function getDefaultData(data){
    return data.map((el, index) => {
        let timeBetweenPassage = "";
        const next = data[index + 1]?.dtCapture;
        if(next){
            timeBetweenPassage = calcTimeBetween(el.dtCapture, next)
        }
        return {...el, timeBetweenPassage }
    })
}

function calcTimeBetween(current, next) {
    if (!next) return "";

    const currentDate = parse(
        current,
        "dd/MM/yyyy HH:mm:ss",
        new Date()
    );

    const nextDate = parse(
        next,
        "dd/MM/yyyy HH:mm:ss",
        new Date()
    );


    const diffInSeconds = differenceInSeconds(
        new Date(currentDate),
        new Date(nextDate)

    );

    if (diffInSeconds <= 0) return "0s";

    const hours = Math.floor(diffInSeconds / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;

    return [
        hours > 0 && `${hours}h`,
        minutes > 0 && `${minutes}min`,
        seconds > 0 && `${seconds}s`
    ].filter(Boolean).join(" ");
}

export function buildGlyphMap({
                                  data,
                                  orderedData,
                                  syncWithGrid
                              }) {
    const LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let labelIndex = 0;

    const source = syncWithGrid ? orderedData : data;

    const glyphByLatLng = new Map();
    const glyphById = new Map();

    for (const item of source) {
        const lat = Number(item.latitude);
        const lng = Number(item.longitude);
        const key = `${lat.toFixed(6)}_${lng.toFixed(6)}`;

        if (!glyphByLatLng.has(key)) {
            glyphByLatLng.set(key, LABELS[labelIndex % LABELS.length]);
            labelIndex++;
        }

        glyphById.set(item.id, glyphByLatLng.get(key));
    }

    return glyphById;
}


export function getMarkersData(data, colorRegistry, zIndexRegistry,  options, replicatedPlates, glyphMap) {
    const markers = new Map();

    data.forEach((item) => {
        const plate = item.plate.rawValue;

        if(options.groupByPlate && !plate) return;

        const lat = Number(item.latitude);
        const lng = Number(item.longitude);

        let marker = markers.get(item.id);
        if (!marker) {
            marker = {
                lat,
                lng,
                id: item.id,
                data: item,
                plate: item.plate,
                glyphText: glyphMap.get(item.id),
                zIndex: zIndexRegistry.current.get(item.id) ?? 10,
                color: resolveMarkersColor(item, colorRegistry, options, replicatedPlates)
            };

            markers.set(item.id, marker);
        }
    })

    return markers;
}

export function groupDataByPlate(data) {
    const map = new Map();

    data.forEach(el => {
        const plate = el?.plate?.rawValue;

        if (!plate) return;

        if (!map.has(plate)) {
            map.set(plate, {
                id: plate,
                plate,
                formattedValue: el.plate.formattedValue,
                qtd: 0,
                passages: [],
                isFromCortex: false
            });
        }

        const group = map.get(plate);
        group.qtd++;
        group.passages.push(el);

        group.isFromCortex ||= el.isFromCortex ?? false;
    });

    return Array.from(map.values());
}

export function traceRoute(passages, colorRegistry, options, replicatedPlates, zIndexRegistry) {
    const map = new Map();

    passages.forEach(passage => {
        const plate = passage?.plate?.rawValue;
        if (!plate) return;

        if (!map.has(plate)) {
            map.set(plate, {
               id: plate,
               path: [],
               data:[],
               options: {
                   strokeColor: resolveMarkersColor(passage, colorRegistry, options, replicatedPlates),
                   strokeWeight: 3,
                   strokeOpacity: 1,
                   zIndex: zIndexRegistry.current.get(passage.id) ?? 10
               }
            });
        }

        const lat = Number(passage.latitude.toFixed(6));
        const lng = Number(passage.longitude.toFixed(6));

        const polyline = map.get(plate);
        polyline.path.push({ lat, lng });
        polyline.data.push(passage);
    });

    return Array.from(map.values());
}
export function generateHexColorFromPlate(plate = "") {
    let hash = 0;

    for (let i = 0; i < plate.length; i++) {
        hash = plate.charCodeAt(i) + ((hash << 5) - hash);
    }

    const min = 80;
    const max = 255;
    const range = max - min;

    const r = min + (hash & 0xff) % range;
    const g = min + ((hash >> 8) & 0xff) % range;
    const b = min + ((hash >> 16) & 0xff) % range;

    return `#${[r, g, b]
        .map(v => v.toString(16).padStart(2, "0"))
        .join("")}`;
}

export function resolveMarkersColor(item, colorRegistry, options, replicatedPlates){
    const plateId = options.groupByPlate ? item.plate.rawValue: item.id;
    return resolveColor(plateId, colorRegistry.current, options,replicatedPlates);
}

export function resolveLineColor(item, colorRegistry, options, replicatedPlates){
    return resolveColor(item.plate, colorRegistry.current, options,replicatedPlates);
}

export function resolveGridColor(item, colorRegistry, options, replicatedPlates){
    const plateId = item.id;
    return resolveColor(plateId, colorRegistry.current, options, replicatedPlates);
}
export function resolveColor(item, colorRegistry, options, replicatedPlates) {
    if (options.groupByPlate) {
        return (
            colorRegistry.grouped.get(item)
            ?? (replicatedPlates?.has(item)
                ? generateHexColorFromPlate(item)
                : "#dedede")
        );
    }

    return (
        colorRegistry.default.get(item)
        ?? "#dedede"
    );
}

const EXIT = getAssetPath("img/sentry_lane/EXIT.png");
const ENTRANCE = getAssetPath("img/sentry_lane/ENTRANCE.png");
const INTERNAL = getAssetPath("img/sentry_lane/INTERNAL.png");
const INTERSECTION = getAssetPath("img/sentry_lane/INTERSECTION.png");
const MOBILE = getAssetPath("img/sentry_lane/MOBILE.png");

export const regionType = {
    'EXIT': EXIT,
    'INTERNAL': INTERNAL,
    'INTERSECTION': INTERSECTION,
    'MOBILE': MOBILE,
    'ENTRANCE': ENTRANCE,
}




